import React, { useRef, useCallback } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import SearchResultItem from "./SearchResultItem";
import { useNavigate } from "react-router";
import SearchResultSkeleton from "./SearchResultSkeleton";

const PROJECT_SETTINGS = `?info=true&tab=settings`;
const INBOUND_SETTINGS = `?info=true&tab=inbound`;

const capturePath = id => `/projects/${id}/capture-inventory`;
const scrubberPath = id => `/projects/${id}/assets`;

export default function SearchResults({
  loading,
  searchTerm,
  searchResults,
  hasMore,
  metadata,
  loadMore,
  onClose,
}) {
  const navigate = useNavigate();
  const observer = useRef();

  const lastResultElementRef = useCallback(
    node => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && hasMore) {
          loadMore();
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore, loadMore]
  );

  const handleResultClick = async result => {
    onClose();
    const isScrubber = window.location.pathname.includes("/assets");
    navigate({ pathname: "/loading" });
    await new Promise(resolve => setTimeout(resolve, 0));

    if (result.__typename === "Project") {
      const pathname = isScrubber ? scrubberPath(result.id) : capturePath(result.id);
      navigate({ pathname, search: PROJECT_SETTINGS });
    } else if (result.__typename === "Asset") {
      const projectId = result.project.id;
      const pathname = isScrubber ? scrubberPath(projectId) : capturePath(projectId);
      navigate({ pathname, search: `?search=${result.itemId}` });
    } else if (result.__typename === "Shipment") {
      const projectId = result.project.id;
      const pathname = isScrubber ? scrubberPath(projectId) : capturePath(projectId);
      navigate({ pathname, search: `${INBOUND_SETTINGS}&inboundSearch=${result.shipmentId}` });
    }
  };

  if (searchTerm.length === 0) {
    return null;
  }

  if (searchResults.length > 0) {
    return (
      <>
        <List sx={{ mt: 1 }}>
          {searchResults.map((result, index) => {
            return (
              <SearchResultItem
                key={`${result.__typename}-${result.id}`}
                result={result}
                onClick={handleResultClick}
              />
            );
          })}
        </List>
        {hasMore && (
          <Box ref={lastResultElementRef}>
            <SearchResultSkeleton />
          </Box>
        )}
      </>
    );
  }

  if (loading) {
    return (
      <Box mt={1}>
        <SearchResultSkeleton />
      </Box>
    );
  }

  return (
    <>
      {!loading && searchTerm.trim().length > 0 && metadata.totalCount === 0 && (
        <Box sx={{ p: 2, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            No results found
          </Typography>
        </Box>
      )}
    </>
  );
}
